import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Bird, Wallet, HeartPulse, HardHat, Boxes, Warehouse, Sprout,
  FileText, BarChart3, Bell, Settings as SettingsIcon, MapPin, Map, ShieldCheck,
  Rocket, Radar, Users, KeyRound, Megaphone, Briefcase, LogOut, ChevronDown, Search,
  Menu, X, Sparkles, ChevronRight, Gauge,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import ThemeToggle from "@/components/premium/ThemeToggle";
import { Badge, Button } from "@/components/premium/UI";
import { toast } from "sonner";

// permission key -> nav item
const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/",                 label: "Operating Centre",     perm: "module:dashboard",  icon: Gauge,           module: "situation" },
      { to: "/dashboard",        label: "Dashboard",            perm: "module:dashboard",  icon: LayoutDashboard, module: "dashboard" },
      { to: "/situation",        label: "Situation Room",       perm: "module:situation",  icon: Radar,           module: "situation" },
    ],
  },
  {
    label: "Executive Intelligence",
    items: [
      { to: "/intelligence", label: "Intelligence",           perm: "module:intelligence", icon: Radar,           module: "situation" },
      { to: "/knowledge",    label: "Knowledge360",           perm: "module:knowledge",    icon: FileText,        module: "reports" },
      { to: "/briefs",       label: "Executive Briefs",       perm: "module:briefs",       icon: FileText,        module: "reports" },
      { to: "/feedback",     label: "Feedback register",      perm: "module:dashboard",    icon: FileText,        module: "settings" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/health",      label: "Health360",           perm: "module:health",     icon: HeartPulse,      module: "health" },
      { to: "/sales",       label: "Sales & Cash",        perm: "module:sales",      icon: Wallet,          module: "sales" },
      { to: "/poultry",     label: "Poultry360",          perm: "module:poultry",    icon: Bird,            module: "poultry" },
      { to: "/projects",    label: "Projects360",         perm: "module:projects",   icon: HardHat,         module: "projects" },
      { to: "/inventory",   label: "Inventory360",        perm: "module:inventory",  icon: Boxes,           module: "inventory" },
      { to: "/assets",      label: "Assets360",           perm: "module:assets",     icon: Warehouse,       module: "assets" },
      { to: "/farm",        label: "Farm360",             perm: "module:farm",       icon: Sprout,          module: "farm" },
    ],
  },
  {
    label: "People & Growth",
    items: [
      { to: "/hr",          label: "HR360",               perm: "module:hr",         icon: Briefcase,       module: "hr" },
      { to: "/marketing",   label: "Marketing360",        perm: "module:marketing",  icon: Megaphone,       module: "marketing" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/finance",     label: "Finance360",          perm: "module:finance",    icon: Wallet,          module: "finance" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/analytics",   label: "Analytics",           perm: "module:analytics",  icon: BarChart3,       module: "reports" },
      { to: "/reports",     label: "Reports",             perm: "module:reports",    icon: FileText,        module: "reports" },
      { to: "/documents",   label: "Documents",           perm: "module:documents",  icon: FileText,        module: "reports" },
      { to: "/notifications", label: "Notifications",     perm: "module:notifications", icon: Bell,         module: "reports" },
    ],
  },
  {
    label: "Property",
    items: [
      { to: "/property",    label: "Property Master",     perm: "module:property",   icon: MapPin,          module: "map" },
      { to: "/map",         label: "Property Map",        perm: "module:map",        icon: Map,             module: "map" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/users",       label: "User Management",     perm: "module:users",      icon: Users,           module: "settings" },
      { to: "/permissions", label: "Permissions",         perm: "module:permissions",icon: KeyRound,        module: "settings" },
      { to: "/audit",       label: "Audit Log",           perm: "module:audit",      icon: ShieldCheck,     module: "settings" },
      { to: "/go-live",     label: "Go-Live",             perm: "module:go_live",    icon: Rocket,          module: "settings" },
      { to: "/settings",    label: "Settings",            perm: "module:settings",   icon: SettingsIcon,    module: "settings" },
    ],
  },
];

function BrandBadge() {
  return (
    <div className="brand-badge flex items-center gap-2.5">
      <div className="relative h-9 w-9 grid place-items-center rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/20">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="brand-badge-title font-heading text-[15px] font-bold leading-none tracking-tight">Home360<span className="text-accent">™</span></div>
        <div className="brand-badge-subtitle text-[10px] text-muted-foreground mt-0.5 tracking-wide">Digital Operations Platform</div>
      </div>
    </div>
  );
}

function SearchBar() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function run(query) {
    if (!query || query.trim().length < 2) { setResults(null); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`);
      setResults(data);
      setOpen(true);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  }

  return (
    <div className="relative flex-1 max-w-lg">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="global-search-input"
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); run(e.target.value); }}
          onFocus={() => q && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search across your permitted data…"
          className="w-full h-10 rounded-full border border-border bg-card pl-10 pr-4 text-sm placeholder:text-muted-foreground ring-focus"
        />
      </div>
      {open && results && (
        <div className="absolute z-40 top-12 left-0 right-0 rounded-2xl border border-border bg-popover shadow-2xl overflow-hidden animate-fade-in">
          <div className="px-4 py-2 border-b border-border text-[11px] eyebrow flex items-center justify-between">
            <span>{results.results?.length || 0} results across {results.scopes_searched?.length || 0} scopes</span>
            {busy && <span className="text-muted-foreground">Searching…</span>}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {(results.results || []).length === 0 ? (
              <div className="px-4 py-6 text-sm text-muted-foreground text-center">No results — you either don't have permission or nothing matches.</div>
            ) : results.results.map((r, i) => (
              <div key={i} className="px-4 py-2.5 hover:bg-muted/50 border-b border-border/40 last:border-b-0 flex items-center gap-3">
                <Badge variant="primary">{r.kind}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title || "(untitled)"}</div>
                  {r.subtitle && <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const { roleName } = usePermissions();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!user) return null;
  const initials = (user.name || user.username || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="relative">
      <button
        data-testid="user-menu-button"
        onClick={() => setOpen(v => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className="inline-flex items-center gap-2.5 rounded-full pl-1 pr-3 py-1 border border-border hover:bg-muted/50 transition-colors ring-focus"
      >
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary to-accent grid place-items-center text-white font-heading text-xs font-bold">{initials}</div>
        <div className="hidden sm:block text-left leading-tight">
          <div className="text-xs font-semibold truncate max-w-[140px]">{user.name || user.username}</div>
          <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">{roleName || user.role}</div>
        </div>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-border bg-popover shadow-2xl overflow-hidden animate-fade-in z-50">
          <div className="p-3 border-b border-border">
            <div className="text-sm font-semibold truncate">{user.name || user.username}</div>
            <div className="text-xs text-muted-foreground truncate">{user.email}</div>
          </div>
          <button onClick={() => nav("/settings")} className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 inline-flex items-center gap-2">
            <SettingsIcon className="w-4 h-4" /> Settings
          </button>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); toast.success("Signed out"); nav("/login"); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 inline-flex items-center gap-2 text-destructive"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Sidebar({ mobile, onNav }) {
  const { has } = usePermissions();
  const location = useLocation();
  const groups = useMemo(() => NAV_GROUPS.map(g => ({
    ...g,
    items: g.items.filter(i => has(i.perm)),
  })).filter(g => g.items.length), [has]);

  return (
    <aside className={`${mobile ? "" : "hidden lg:flex"} relative flex-col w-64 exec-sidebar sticky top-0 h-screen`}>
      <div className="p-4 border-b sidebar-divider relative z-[1]">
        <BrandBadge />
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-4 relative z-[1]" data-testid="sidebar-nav">
        {groups.map(group => (
          <div key={group.label}>
            <div className="sidebar-eyebrow eyebrow px-3 mb-1.5">{group.label}</div>
            <ul className="space-y-0.5">
              {group.items.map(item => {
                const active = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      data-testid={`nav-${item.to.replace(/^\//, "").replace(/\//g, "-") || "home"}`}
                      onClick={onNav}
                      className={({ isActive }) =>
                        `nav-item group flex items-center gap-3 px-3 py-2 text-sm ring-focus ${(isActive || active) ? "is-active" : ""}`
                      }
                    >
                      <Icon className="nav-icon w-4 h-4" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {active && <ChevronRight className="w-3.5 h-3.5 opacity-70" />}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t sidebar-divider relative z-[1]">
        <div className="flex items-center justify-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-300">RC3 · Operational Pilot</span>
        </div>
        <div className="sidebar-footer-text text-[10px] text-center mt-1">© {new Date().getFullYear()} Home360™ · Thom Homestead</div>
      </div>
    </aside>
  );
}

function NotificationBell() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const { data } = await api.get("/notifications?unread_only=true&limit=50");
        if (alive) setCount(Array.isArray(data) ? data.length : (data?.length || 0));
      } catch { /* silent */ }
    }
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return (
    <Link
      to="/notifications"
      data-testid="header-notifications-btn"
      aria-label={count > 0 ? `Notifications (${count} unread)` : "Notifications"}
      className="relative hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-full border border-border hover:bg-muted/50 ring-focus"
    >
      <Bell className="w-4 h-4" />
      {count > 0 && (
        <span
          data-testid="header-notifications-badge"
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold grid place-items-center border-2 border-background"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

export default function AppShell({ children }) {
  const [drawer, setDrawer] = useState(false);
  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar />
      {/* mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-72 shadow-2xl animate-fade-in">
            <Sidebar mobile onNav={() => setDrawer(false)} />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 glass-header">
          <div className="flex items-center gap-3 px-4 sm:px-6 py-3">
            <button
              className="lg:hidden inline-flex items-center justify-center rounded-lg h-9 w-9 border border-border hover:bg-muted/50"
              onClick={() => setDrawer(true)}
              data-testid="mobile-menu-button"
            >
              {drawer ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            <div className="lg:hidden"><BrandBadge /></div>
            <SearchBar />
            <div className="ml-auto flex items-center gap-2">
              <ThemeToggle compact />
              <NotificationBell />
              <UserMenu />
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full mx-auto animate-fade-in" data-testid="app-main">
          {children}
        </main>
      </div>
    </div>
  );
}
